<script setup lang="ts">
import { onBeforeMount, onMounted, onUnmounted, ref } from 'vue'

import { CancelModalError } from '../../composables'
import { getHandlePressed, isPressedEscape } from '../../utils'
import Button from '../Button/Button.vue'
import { modalContainerId } from './consts'

interface Props {
  title?: string
  titleClass?: string
  withFadeInAnimation?: boolean
  withCloseIcon?: boolean
  withCloseShortcuts?: boolean
  confirmButton?: {
    text: string
    confirm: () => Promise<void> | void
    disabled?: boolean
    tooltip?: string
  }
  cancelButton?: {
    text?: string
    cancel?: () => void
  }
}

const props = withDefaults(defineProps<Props>(), {
  withCloseIcon: true,
  withCloseShortcuts: true,
  withFadeInAnimation: true,
  teleportTo: '#modal_container',
  titleClass: 'mb-4',
})

type Emits = { close: [] }
const emit = defineEmits<Emits>()

// data
const cancelButtonText = props.cancelButton?.text || 'Cancel'

const modalContentOffset = 32 // 4rem * 2

// state
const confirmed = ref(false) // if true, the confirm button should be disabled to prevent multiple clicks
const modalContentRef = ref<HTMLDivElement>()
const shouldModalContentStickToTop = ref(false)

const modalBackgroundTopPosition = ref(0)
const modalBackgroundRef = ref<HTMLDivElement>()

// methods
function onCancelClick(): void {
  if (props.cancelButton?.cancel) {
    props.cancelButton?.cancel()
  }

  closeModal()
}

function closeModal(): void {
  emit('close')
}

async function confirm(): Promise<void> {
  confirmed.value = true

  try {
    await props.confirmButton?.confirm()

    closeModal()
  } catch (error) {
    if (error instanceof CancelModalError === false) {
      throw error
    }
  } finally {
    confirmed.value = false
  }
}

function updateShouldStickToTop(): void {
  if (!modalContentRef.value) {
    return
  }

  shouldModalContentStickToTop.value =
    window.innerHeight < modalContentRef.value.clientHeight + modalContentOffset
}

function calculateBackgroundTopPosition(): void {
  const modalHeight = modalBackgroundRef.value?.clientHeight || 0
  const bodyHeight = document.body.clientHeight
  const scrollY = window.scrollY

  modalBackgroundTopPosition.value = Math.min(scrollY, bodyHeight - modalHeight)
}

const closeModalOnEscape = getHandlePressed(isPressedEscape, closeModal)

// init
onBeforeMount(() => {
  // unfocus last clicked element which triggered modal, to prevent opening another modal when pressing Enter
  ;(document.activeElement as HTMLElement)?.blur()
})

onMounted(() => {
  const scrollBarWidth = window.innerWidth - document.body.offsetWidth

  document.body.classList.add('overflow-hidden')
  document.body.style.marginRight = scrollBarWidth + 'px'

  if (props.withCloseShortcuts) {
    document.addEventListener('keydown', closeModalOnEscape)
  }

  /** Firefox positioning fixes */

  if (!import.meta.env.DEV) {
    // disabled on DEV - prevents livereload error after changing modal content
    // TypeError: Cannot set properties of null (setting '__vnode') at patchElement
    // TODO: find cause and fix
    updateShouldStickToTop()
  }

  window.addEventListener('resize', updateShouldStickToTop)
  window.addEventListener('scroll', calculateBackgroundTopPosition)
  window.addEventListener('resize', calculateBackgroundTopPosition)

  calculateBackgroundTopPosition()
})

onUnmounted(() => {
  document.body.classList.remove('overflow-hidden')
  document.body.style.marginRight = 0 + 'px'

  if (props.withCloseShortcuts) {
    document.removeEventListener('keydown', closeModalOnEscape)
  }

  window.removeEventListener('resize', updateShouldStickToTop)
  window.removeEventListener('scroll', calculateBackgroundTopPosition)
  window.removeEventListener('resize', calculateBackgroundTopPosition)
})
</script>

<template>
  <Teleport :to="`#${modalContainerId}`">
    <div
      ref="modalBackgroundRef"
      class="bg-neutral-dark-3/35 absolute left-0 z-20 h-screen w-screen"
      :class="{
        'fade-in-animation': withFadeInAnimation,
      }"
      :style="{ top: modalBackgroundTopPosition + 'px' }"
    >
      <div
        class="grid h-full w-full overflow-y-scroll p-4"
        :style="{
          placeContent: shouldModalContentStickToTop
            ? 'start space-around'
            : 'space-around',
        }"
        @click.self="withCloseShortcuts ? closeModal() : undefined"
      >
        <div
          ref="modalContentRef"
          class="bg-neutral-light-9 text-gray shadow-drop-shadow rounded-2xl p-4"
        >
          <div
            v-if="$slots.title || title || withCloseIcon"
            class="flex min-h-7 justify-end"
            :class="{ [`${titleClass}`]: titleClass }"
          >
            <span class="flex-1">
              <span
                v-if="title"
                class="text-lg font-bold"
              >
                {{ title }}
              </span>
              <slot name="title" />
            </span>
            <Button
              v-if="withCloseIcon"
              class="text-neutral-dark-3/60 hover:text-neutral-2 border-none pl-2 text-lg"
              icon="close"
              iconSize="lg"
              size="xs"
              square
              @click="closeModal"
            />
          </div>
          <div class="min-w-[72vw] @sm:min-w-115">
            <slot />
          </div>
          <div class="flex justify-end gap-4 pt-4">
            <Button
              class="text-neutral-dark-2 bg-neutral-light-10 hover:bg-neutral-light-4 active:bg-neutral-light-6 border-neutral-dark-10 px-9! text-lg font-bold active:transition-none"
              size="sm"
              @click="onCancelClick"
            >
              {{ cancelButtonText }}
            </Button>
            <Button
              v-if="confirmButton"
              class="px-9! text-sm font-bold"
              :class="{
                'text-neutral-light-10 bg-success-2 hover:bg-success-4 active:bg-success-5 active:transition-none':
                  !confirmed && !confirmButton.disabled,
              }"
              size="sm"
              :disabled="confirmed || confirmButton.disabled"
              :title="confirmButton.tooltip"
              @click="confirm"
            >
              {{ confirmButton.text }}
            </Button>
          </div>
        </div>
      </div>
    </div>
  </Teleport>
</template>

<style scoped>
.fade-in-animation {
  animation: fadeIn 100ms ease-in;
}

@keyframes fadeIn {
  0% {
    opacity: 0;
  }
  100% {
    opacity: 1;
  }
}
</style>
